import {
  Box,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import UserImage from "../../../../../UserImage";
import { useDispatch, useSelector } from "react-redux";
import {
  selectIsUserProfile,
  selectProfile,
} from "../../../../../../redux/reducers/gallery";
import TextView from "../../../../../TextView";
import { colors } from "../../../../../../utils";
import { useState } from "react";
import { FaEllipsisV } from "react-icons/fa";
import FlagIcon from "@mui/icons-material/Flag";
import Edit from "@assets/images/editIcon.svg";
import {
  setReportInstance,
  setReportLocation,
  setReportModalOpen,
} from "../../../../../../redux/actions";
import { ASSETS } from "../../../../../../utils/config";
import {
  LINKVAULT_FAVICON_FIX_ENABLED,
  handleFaviconError,
  handleFaviconLoad,
  resolveLinkIconSrc,
} from "../faviconFix";
import { useParams } from "react-router-dom";
import ShareIcon from "@mui/icons-material/Share";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { PATH } from "../../../../../../utils/config";
import { getLoggedInUser } from "../../../../../../utils/helpers";
import {
  resolveVaultBackground,
  resolveVaultAccent,
  resolveEffectiveVaultTheme,
  isSafeCssColor,
  NEUTRAL_VAULT_BACKGROUND,
  NEUTRAL_VAULT_BACKGROUND_FULLPAGE,
} from "./vaultTheme";
import { autoTextColor } from "../../../../../../utils/contrast";
// batch-proof-fe-3d5e0fc5 (Gordon 2026-09-17): profile.gallery_theme is
// persisted in GalleryTheme/galleryTheme.js's OWN legacy shape (bg_color/
// wallpaper/accent -- see that file's header), not the {background,button}
// token shape Theming/themeEngine.js's resolveThemeBackground/resolveColorPair
// expect. Passing the raw gallery_theme through those resolvers (as this file
// did before) always resolved to null for every real account, so "Match the
// Gallery" never actually followed the gallery's colour. Use the SAME
// resolvers the real Gallery page (src/pages/Gallery/index.jsx) renders from.
import {
  resolveGalleryBackground,
  resolveGalleryAccent,
  resolveCardRadius,
} from "../../../../../GalleryTheme/galleryTheme";
// gordon-0918b-gallery-accent-border-all-four-spaces: the ONE shared
// {border, borderRadius} pair every themed gallery card/space (incl.
// LinkVault) uses, so they can't diverge.
import { accentBorderSx } from "../../../../../Theming/themeEngine";

const LinkVaultPreview = ({
  showMenu = false,
  links,
  handleEditLinkVault,
  fullPage = false,
  // When editing, the live (unsaved) theme can be passed in to preview it.
  themeOverride,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const profile = useSelector(selectProfile);
  const isUserProfile = useSelector(selectIsUserProfile);
  // The logged-in user's OWN account portrait — populated at login, the same
  // source Navbar/ProfileMenu already trust — independent of any page-level
  // gallery fetch.
  const loggedInUserProfile = useSelector((state) => state.userProfile?.userProfile);

  const linksToShow = links?.filter((link) => link.is_cta_enabled);

  // Bug fix (Gordon, live web-stg 2026-08-06): this preview's avatar used to
  // read ONLY `profile?.profile_image` (the gallery-slice profile, populated
  // by a page-specific fetch — WheelListing's or LinkVaultPage's effect —
  // that can still be unresolved when this preview mounts, e.g. the Settings
  // modal opened before it lands). That produced a generic placeholder even
  // though the account's real portrait was already sitting in
  // state.userProfile (set at login, the same source Navbar/ProfileMenu
  // already trust). Fall back to it, but ONLY for the CURRENT user's own
  // vault — never when previewing someone else's public LinkVault, where
  // this would leak the VIEWER's own photo onto a stranger's card.
  //
  // "Own vault" is `isUserProfile` (the same flag this component already
  // trusts a few lines down to decide Edit vs Share/Report), defensively
  // re-checked two ways (adversarial qa-validator review, 2026-08-06):
  //   1. Against the loaded gallery profile's user_id — but only skips this
  //      check when the profile hasn't loaded AT ALL (null). A profile that
  //      HAS loaded but carries no user_id is unidentifiable, not "safe" —
  //      failing OPEN on that case would have treated "loaded-but-malformed"
  //      the same as "not loaded yet", which is the wrong default.
  //   2. Against `loggedInUserProfile`'s OWN id — the value actually
  //      CONSUMED (its .profile_image) was never checked before. `redux/
  //      reducers/user.js`'s USER_PROFILE_SUCCESS/EDIT_USER_PROFILE_SUCCESS
  //      cases MERGE (`{...state.userProfile, ...payload}`), so a payload
  //      that omits profile_image retains whatever the PREVIOUS account left
  //      behind. Currently unreachable only because account-switch hard-
  //      reloads (services/accountSwitch.js) — an external property this
  //      component shouldn't depend on to stay safe.
  // Profile/index.jsx separately documents a real historical race where
  // isUserProfile itself can lag a profile-id change by a tick, so the flag
  // alone was never enough to gate a photo leak.
  const loggedInUserId = getLoggedInUser()?.id;
  const isOwnVault =
    isUserProfile &&
    (profile == null ||
      (profile.user_id != null &&
        String(profile.user_id) === String(loggedInUserId))) &&
    (loggedInUserProfile == null ||
      String(loggedInUserProfile.user_id ?? loggedInUserProfile.id) ===
        String(loggedInUserId));
  const avatarSrc =
    profile?.profile_image ||
    (isOwnVault ? loggedInUserProfile?.profile_image : undefined);

  // Resolve the saved (or live-preview) Vault Design theme. An unset theme =>
  // null overrides => today's exact hard-coded look (passthrough).
  const vaultTheme = themeOverride ?? profile?.vault_theme;
  // gordon-0917-themes-popup-add-business-box-and-linkvault: "Match the
  // Gallery" reads match_gallery FIRST, mapping the Gallery's own token theme
  // onto LinkVault's legacy shape; only when it's off (or unset) does the
  // vault's own stored bg_color/wallpaper/accent apply.
  const effectiveVaultTheme = resolveEffectiveVaultTheme(
    vaultTheme,
    profile?.gallery_theme,
    resolveGalleryBackground,
    resolveGalleryAccent
  );
  const themedBackground = resolveVaultBackground(effectiveVaultTheme, { fullPage });
  // resolveVaultAccent already returns only a safe color literal (or null), but
  // we re-validate at the use-site so a non-color value can never reach the
  // `border: 1px solid ${accent}` interpolation below.
  const resolvedAccent = resolveVaultAccent(effectiveVaultTheme);
  const themedAccent = isSafeCssColor(resolvedAccent) ? resolvedAccent : null;
  // Gordon 2026-09-18 (gordon-0918-themes-modal-side-pane-icons-accent-
  // borders-corners-linkvault-bb, item 3): "the Card corners slider must
  // also round the LinkVault... corners (today it only affects the wheel
  // cards)". LinkVault's own theme has no corner_rounding field of its own
  // (unlike Business Box's token theme) -- there's nothing to override, so
  // this always mirrors the Gallery's OWN card_radius when the creator has
  // explicitly set one. Passthrough-safe: an unset gallery_theme.card_radius
  // keeps today's hardcoded 8px exactly (see resolveCardRadius's own
  // DEFAULT_CARD_RADIUS of 12, which would NOT be a true passthrough here).
  const themedCornerRadius =
    profile?.gallery_theme?.card_radius != null
      ? resolveCardRadius(profile.gallery_theme)
      : null;

  // Gordon 2026-09-22 (gordon-0922-P3-gallery-theme-linkvault-accent-border-
  // on-transparent-background-dev-first): resolves the "border-line vs solid
  // fill" question LinkVaultPreview.accentBorderAndCorners.test.jsx left open
  // for Gordon -- the container itself no longer paints a fill (see the Box
  // below), matching the transparent, border-only wrapper Boxes the sibling
  // "profile box" / "featured wheel" spaces already use (WheelListing/
  // index.js). `effectiveSurface` is NOT painted; it's only what the name
  // text is actually composited over (the same value that used to be
  // painted), so autoTextColor can still pick a legible ink against it.
  const effectiveSurface =
    themedBackground || (fullPage ? NEUTRAL_VAULT_BACKGROUND_FULLPAGE : NEUTRAL_VAULT_BACKGROUND);
  const nameInk = autoTextColor(effectiveSurface, { fallback: colors.primary });

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleReport = () => {
    if (!getLoggedInUser()) {
      // Redirect to login page if user is not authenticated
      navigate(PATH.LOGIN);
      return;
    }

    dispatch(setReportModalOpen(true));
    dispatch(setReportInstance(id));
    dispatch(setReportLocation("USER"));
    handleCloseMenu();
  };

  const handleShare = async () => {
    try {
      const baseUrl = window.location.origin;
      const linkVaultUrl = `${baseUrl}/gallery/${id}?view=linkvault`;
      await navigator.clipboard.writeText(linkVaultUrl);

      toast.success("LinkVault URL copied!", {
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      handleCloseMenu();
    } catch (error) {
      console.error("Failed to copy URL:", error);
      toast.error("Failed to copy URL");
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        // Gordon 2026-09-22 (gordon-0922-P3-...-dev-first, DECISION): the
        // container is always transparent now -- an accent border line, not
        // a solid fill -- so the Gallery's own wallpaper shows through, the
        // same way the sibling "profile box" / "featured wheel" wrapper
        // Boxes (WheelListing/index.js) already have no fill of their own.
        // A vault_theme/gallery_theme bg_color or wallpaper (themedBackground)
        // no longer paints this container; it still feeds `nameInk`'s
        // auto-contrast pick above, since that's what actually ends up
        // behind the text once composited.
        background: "transparent",
        height: "100%",
        width: "100%",
        p: "32px 24px",
        // Gordon 2026-09-18 (item 2): "the LinkVault border line" must take
        // the accent, same as the profile box / wheel card outer borders --
        // previously this was a hardcoded neutral gray AND was disabled
        // entirely in fullPage mode (the standalone LinkVault page), so the
        // live page never showed a border at all. Always render it now; a
        // raw (never contrast-swapped) accent, matching the wheel card's own
        // border and this file's existing per-row CTA border below.
        // gordon-0918b-gallery-accent-border-all-four-spaces: shared
        // accentBorderSx (Theming/themeEngine.js) so this line can't drift
        // from the Profile / Featured Wheel / Business Box / Wheel Grid
        // borders -- LinkVault passes its own already-resolved JS-level
        // accent/radius (not the --gt-card-border CSS var) since it also
        // renders standalone (fullPage) outside the Gallery page's themed
        // Grid.
        ...accentBorderSx({
          accent: themedAccent,
          fallback: "#E1E1E1",
          radiusPx: themedCornerRadius,
          radiusFallbackPx: 8,
        }),
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        gap: "20px",
        overflowY: "auto",
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <UserImage size={80} image={avatarSrc} />
      <TextView
        variant="title"
        style={{
          color: nameInk,
          WebkitLineClamp: 2,
          textAlign: "center",
          maxWidth: "100%",
          flexShrink: 0,
        }}
      >
        {profile?.first_name} {profile?.last_name}
      </TextView>

      {showMenu && (
        <>
          <IconButton
            style={{
              color: "#123051",
              width: "34px",
              height: "34px",
              backgroundColor: "#FFFFFF",
              position: "absolute",
              top: "32px",
              right: "24px",
            }}
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
            }}
          >
            <FaEllipsisV
              color="#565556"
              style={{ cursor: "pointer", width: "20px", height: "16px" }}
            />
          </IconButton>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleCloseMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            TransitionComponent={Fade}
            timeout={{ enter: 100, exit: 300 }}
            PaperProps={{
              style: {
                borderRadius: "8px",
                width: "100px",
                transition: "box-shadow 0.3s ease-in-out",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            {isUserProfile && (
              <MenuItem
                onClick={() => {
                  handleEditLinkVault();
                  handleCloseMenu();
                }}
                sx={{
                  fontSize: "14px",
                  color: "#333",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                <img
                  style={{ marginRight: "0.2rem" }}
                  src={Edit}
                  className="img-fluid"
                  alt="Edit"
                  width="15"
                  height="15"
                />
                Edit
              </MenuItem>
            )}

            {!isUserProfile && (
              <>
                <MenuItem
                  onClick={handleShare}
                  sx={{
                    fontSize: "14px",
                    color: "#333",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <ShareIcon
                    sx={{
                      marginRight: "0.2rem",
                      width: "18px",
                      height: "18px",
                      color: "#565556",
                    }}
                  />
                  Share
                </MenuItem>
                <MenuItem
                  onClick={handleReport}
                  sx={{
                    fontSize: "14px",
                    color: "#333",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <FlagIcon
                    sx={{
                      marginRight: "0.2rem",
                      width: "18px",
                      height: "18px",
                      color: "#BBBBBB",
                    }}
                  />
                  Report
                </MenuItem>
              </>
            )}
          </Menu>
        </>
      )}

      {linksToShow?.length > 0 && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {linksToShow.map((link, index) => (
            <Box
              key={index}
              onClick={() => link.link && window.open(link.link, "_blank")}
              sx={{
                width: "100%",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "12px 6px 12px 12px",
                minHeight: "60px",
                backgroundColor: "#FFFFFF",
                // Accent applies to the CTA card border when set; null accent
                // falls back to today's neutral border (passthrough). Shared
                // accentBorderSx (gordon-0918b-gallery-accent-border-all-
                // four-spaces) -- same helper as this file's outer container
                // border above.
                ...accentBorderSx({
                  accent: themedAccent,
                  fallback: "#BBBBBB",
                  radiusPx: themedCornerRadius,
                  radiusFallbackPx: 8,
                }),
                cursor: link.link ? "pointer" : "default",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: "12px",
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={resolveLinkIconSrc(link.icon, link.link) || ASSETS.website}
                  alt={link.title}
                  onError={handleFaviconError(ASSETS.website)}
                  onLoad={
                    LINKVAULT_FAVICON_FIX_ENABLED
                      ? handleFaviconLoad(ASSETS.website)
                      : undefined
                  }
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Typography
                sx={{
                  textAlign: "left",
                  color: "#344054",
                  fontSize: "16px",
                  fontWeight: "500",
                  fontFamily: "DM Sans",
                  pl: 4, // Left padding to account for image space (12px + 24px + extra buffer)
                  pr: 1,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  hyphens: "auto",
                }}
              >
                {link.title || "Untitled"}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LinkVaultPreview;
